import { AppProps } from 'next/app';
import { NextPageWithLayout } from './page';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import React, { useEffect, useRef, useState } from 'react';
import ErrorBoundary from 'Components/blocks/Error/ErrorBoundary';
import { PersistGate } from 'redux-persist/integration/react';
import { persistentStore } from 'src/store/store';
import '../../sass/styles.scss';
import { usePostHog } from 'posthog-js/react';

const BootstrapLoading = dynamic(() => import('Components/shared/BootstrapLoading/BootstrapLoading'), {
  ssr: false,
});
const Poller = dynamic(() => import('Components/shared/Poller/Poller'), { ssr: false });
const SessionLimit = dynamic(() => import('Components/shared/SessionLimit/SessionLimit'));
const Iframe = dynamic(() => import('Components/shared/Iframe/Iframe'));
const OverlayManagerComponent = dynamic(() => import('Components/shared/Overlay/OverlayManagerComponent'), {
  ssr: false,
});
import { useTranslation } from 'react-i18next';
import {
  bootstrapApplication,
  GET_PAYNPLAY_LIMIT,
  getCountryList,
  getStrapiTitles,
  getUserInfo,
  IApplicationState,
  listGamesByTag,
  listIntegrationKeys,
  listLanguages,
  listTranslations,
  logOut,
} from '@spinwise/spinwise-core';
import * as gtag from 'Lib/gtag';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { logoutChannel, logoutListener } from 'src/channels/logout.channel';
import { Subject, takeUntil } from 'rxjs';
import AlertsManager from 'Components/shared/AlertsManager/AlertsManager';
import { ToastNotificationManager } from 'Components/shared/Header/ToastNotificationManager/ToastNotificationManager';
import LiveAgent from 'Lib/zendesk';
import Script from 'next/script';
import { setCookie } from 'cookies-next';

import CountryBlock from 'Components/pages/CountryBlock/CountryBlock';
import LogoutListener from 'Components/shared/LogoutListener';
import useProcessPixels from 'Hooks/useProcessPixels';
import VisitTracker from 'Components/shared/VisitTracker/VisitTracker';
import axios from 'axios';
import { recentSearchesLocalStorageId, RegTypes } from 'Types/globals';
import { PostHogProviderWrapper } from 'Components/shared/PostHogProviderWrapper/PostHogProviderWrapper';

interface AppManagerProps extends AppProps {
  Component: NextPageWithLayout;
  countryData?: any;
  i18n?: any;
  forcedLocale: string;
  seonSourceUrl?: string;
  umami_id?: string;
}

const AppManager = ({
  Component,
  countryData,
  i18n,
  forcedLocale,
  pageProps,
  seonSourceUrl,
  umami_id,
}: AppManagerProps) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const [logoutLoading, setLogoutLoading] = useState(false);
  const [unmounted] = useState(new Subject<void>());
  const { t, i18n: i18n2 } = useTranslation();
  const posthog = usePostHog();
  useProcessPixels(posthog);
  const strapiLoaded = useSelector((state: IApplicationState) => state?.strapiReducer?.loaded);
  const accountId = useSelector((state: IApplicationState) => state.accountReducer?.info?.id);
  const accountEmail = useSelector((state: IApplicationState) => state.accountReducer?.info?.email);
  const isAuthorized = useSelector((state: IApplicationState) => state.authReducer.auth?.success);
  const auth = useSelector((state: IApplicationState) => state.authReducer?.auth);
  const geo = useSelector((state: IApplicationState) => state.globalReducer?.geo);

  const tokenExpiresRef = useRef<string>(null);
  const refreshTokenInProgressRef = useRef<boolean>(false);
  const isTokenValid = () =>
    tokenExpiresRef?.current ? !(new Date(tokenExpiresRef.current).getTime() - new Date().getTime() < 0) : false;

  const requestTokenRefresh = async () => {
    if (!isTokenValid() && !refreshTokenInProgressRef.current) {
      try {
        refreshTokenInProgressRef.current = true;
        const response = await axios.get('/api/account/token/refresh');
        const tokenExpires = response?.data?.tokenExpires;
        const success = response?.data?.success;
        if (success && tokenExpires) {
          tokenExpiresRef.current = tokenExpires;
        } else {
          logoutChannel.send({ action: 'logout_start', loaded: true });
        }
      } catch (e) {
        console.error(e);
      } finally {
        refreshTokenInProgressRef.current = false;
      }
    }
  };
  useEffect(() => {
    i18n2.changeLanguage(router.locale);
  }, [i18n]);

  useEffect(() => {
    // if guest, but auth is success, clear cache
    if (isAuthorized && typeof window !== 'undefined' && window?.frameElement === null) {
      // Token refresh should happen first, before
      // any other request takes place
      requestTokenRefresh();
      if (!countryData?.isGuest) {
        getUserInfo()(dispatch);
      } else {
        logoutChannel.send({ action: 'logout_start', loaded: true });
      }
    }

    if ([RegTypes.PAYPLAY_T, RegTypes.PAYPLAY_Z].includes(countryData?.reg_type)) {
      dispatch({ type: GET_PAYNPLAY_LIMIT });
    }

    listLanguages()(dispatch);
    listTranslations()(dispatch);
    bootstrapApplication()(dispatch);
    listIntegrationKeys()(dispatch);
    listGamesByTag({
      tag: 'games-suggestions',
      showBonusAllowed: false,
      showAvailableGames: false,
      itemsPerPage: 12,
    })(dispatch);
    getStrapiTitles('winnerz', countryData?.locale, countryData?.code2)(dispatch);
    getCountryList(false)(dispatch);

    // here, we would set the store by dispatch ourselves from data
    // received from the SSR
    dispatch({ type: 'BOOTSTRAP_APPLICATION_SUCCESS', data: countryData });
    dispatch({ type: 'LIST_TRANSLATIONS_SUCCESS', data: i18n });

    // unless defualt cookie exists?
    let locale = forcedLocale;

    if (countryData?.locale !== locale && locale === 'en') {
      locale = countryData?.locale;
    }

    if (router?.locale !== locale) {
      const { pathname, query, asPath } = router;
      i18n2.changeLanguage(locale);
      setCookie('locale', locale);
      router.push({ pathname, query }, asPath, { locale: locale });
    }
    const handleWindowMessage = (event: MessageEvent<{ homepageInsideIframe: boolean }>) => {
      if (event?.data?.homepageInsideIframe) {
        router.reload();
      }
    };

    window.addEventListener('message', handleWindowMessage);

    gtag.setVariables({
      auth: auth,
      user: {
        id: accountId,
        email: accountEmail,
      },
    });

    logoutListener.pipe(takeUntil(unmounted)).subscribe(msg => {
      switch (msg.action) {
        case 'logout_start':
          if (msg.loaded) {
            initiateLogoutProcess();
          }
          break;
        case 'loaded_changed':
          if (msg.loaded && msg.logoutStart) {
            initiateLogoutProcess();
          }
          break;
      }
    });

    if (countryData?.isMirror === 'true') {
      localStorage?.setItem('is-mirror', 'true');
    } else {
      localStorage?.removeItem('is-mirror');
    }

    return () => {
      window.removeEventListener('message', handleWindowMessage);
      unmounted.next();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initiateLogoutProcess = () => {
    setLogoutLoading(true);
    posthog?.reset();

    logOut()(dispatch, {
      onBeforeUpdate: () => {
        localStorage.setItem('sessionTime', '0');
        localStorage.removeItem(recentSearchesLocalStorageId);
      },
      onAfterUpdate: () => {
        LiveAgent.logOutUser();
        // clear clientside
        document.cookie = '_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        router.reload();
      },
    });
  };

  const renderPage = (guestOnly: boolean) => {
    const { isGuest, code } = countryData;
    const WithLayout = Component.getLayout || (page => page);
    //@ts-ignore
    if ([512, 513].includes(code) || [512, 513].includes(geo?.code)) {
      // this comes as number if denied

      return (
        <CountryBlock
          geo={{
            ...countryData,
            ...geo,
          }}
        />
      );
    }

    if (!isGuest && guestOnly) {
      return false;
    }
    return WithLayout(
      <PostHogProviderWrapper>
        <BootstrapLoading isLoading={!strapiLoaded || logoutLoading} />
        <OverlayManagerComponent />
        <Component {...pageProps} />
        <SessionLimit />
        <AlertsManager />
        <ToastNotificationManager />
        <Poller
          refreshTokenInProgress={refreshTokenInProgressRef.current}
          isTokenValid={isTokenValid}
          requestTokenRefresh={requestTokenRefresh}
        />
        <Iframe />
        <LogoutListener forcedLocale={forcedLocale} />
      </PostHogProviderWrapper>,
    );
  };

  return (
    <>
      <Head>
        <title>{t('page.main.meta.title')}</title>
        <meta name="description" content={t('page.main.meta.description')} />
      </Head>
      <ErrorBoundary>
        <PersistGate loading={renderPage(true)} persistor={persistentStore}>
          {renderPage(false)}
        </PersistGate>
        {isAuthorized !== true ? <Script src={seonSourceUrl} strategy="lazyOnload" /> : null}
        {isAuthorized !== true ? <VisitTracker id={umami_id} /> : null}
      </ErrorBoundary>
    </>
  );
};

export default AppManager;
